import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { createPortal } from "react-dom";
import type { ApolloClient } from "@apollo/client";

import { trackEvent } from "../telemetry/appInsights";
import type { LoadedMemberForResolve } from "../members/resolvePpMembId";
import type { PpClaimMemberSeed, PpClaimRecordLike } from "../members/toClaimContext";
import { checkClaimMemberAccess } from "./claimMemberAccess";
import {
  claimProviderAddress,
  claimProviderKey,
  claimProviderLabel,
  selectDefaultClaimProvider,
  type ClaimProvider,
} from "./claimProviderList";
import { useClaimProviderList } from "./useClaimProviderList";
import { usePpClaimMemberSeed } from "./usePpClaimMemberSeed";

/**
 * The Create Claim ENTRY GATE — the port of Provider Portal's
 * `CreateClaimModal` gate chain (providerPortalFrontend
 * `frontend/src/components/Claims/CreateClaimModal/index.tsx`), minus its member
 * search (member-360 already has the member in hand; the entry point lives on
 * the member's own Claims tab).
 *
 * It exists because @astrana/ui's native `CreateClaim` module will happily mount
 * and fire `GET ClaimMember/CreateClaim` with `provKeyId: ""` / `hpCode: ""` —
 * an unconditional HTTP 400 — and because that seed endpoint applies no
 * entitlement check of its own (only `tokenUserObjectId == userId` plus non-blank
 * params). So this gate is the only thing standing between a user and an OPEN,
 * SEEDED claim form.
 *
 * WHAT IT IS NOT: the last line of defense before a claim is FILED. PP-API's
 * `SaveClaim` re-authorizes the billing provider server-side and fails closed
 * (see claimProviderList.ts's header for the specifics) — that check is the
 * stronger one and must not be relaxed on the strength of this one. What this
 * gate uniquely owns is (a) supplying a valid `provKeyId` at all, (b) the
 * payer-routing health-plan rules, which the server does NOT enforce for
 * HNCC / BSPP / Caremore, and (c) not walking a user through a CMS-1500 whose
 * submit was always going to be rejected.
 *
 * Three things must be true BEFORE the form mounts:
 *
 *   1. The member RESOLVES to exactly one Provider Portal member row, and that
 *      row carries all three values the claim shell endpoint needs —
 *      `RmiMembKeyId`, `RmiHpCode` and `RmiStartDate` (see
 *      `usePpClaimMemberSeed`). They are taken from that row TOGETHER because
 *      `spRmiCreateTempClaim` matches the member on them as a COMPOSITE key and
 *      INSERTs whether or not it matched — a mixed-source or approximate trio
 *      produces an HTTP 200 with a blank-patient claim shell, which is worse
 *      than the 400 a missing param would have produced.
 *   2. That row's health plan is one Astrana adjudicates
 *      (`checkClaimMemberAccess`, verbatim legacy rules + copy). The code being
 *      PP-sourced is what makes this check meaningful: it is EZCap's own
 *      `RmiHpCode`, not a free-text health plan name from another system.
 *   3. The acting user holds ≥1 claim-WRITE provider grant in this IPA, and one
 *      of them is selected — its `RmiProvKeyId` GUID becomes the claim's
 *      billing provider / facility / provider NPI.
 *
 * Note what is NOT a precondition: a linked Provider Portal USER account. The
 * member-row resolution runs through care-management-gql, which mints a PP
 * service-account token for Entra-SSO callers, so it works for unlinked SSO
 * users. A missing user-level PP token blocks separately, as the provider list's
 * `no-identity`.
 *
 * FAIL CLOSED. Every failure mode (endpoint not deployed, 403, network, empty
 * grant list, unknown health plan, no tenant) ends in a block with an
 * explanation and no way through. Nothing here ever falls through to opening the
 * form, and `Continue` is disabled in every status except `"ready"`-with-a-
 * VISIBLE-selection.
 *
 * Layout is stacked and narrow-first so it stays usable in the Chrome-extension
 * side panel (~340-400px) — deliberately not legacy's 1300px-wide table. That
 * same constraint is why every control carries an explicit ≥44px minimum tap
 * target. Portaled to <body> per CLAUDE.md (a fixed full-viewport overlay
 * rendered inside @astrana/ui's `@container` subtree re-rasterizes the
 * background text).
 */
export interface CreateClaimGateProps {
  /** For the dialog subtitle. Optional — never required to proceed. */
  memberName?: string;
  /**
   * The APP-level Apollo client, used to resolve the member's PP row. Must NOT
   * be a client from inside @astrana/ui's own GraphQLProvider subtree (see
   * lib/apollo/GraphQLClientGate).
   */
  client: ApolloClient<unknown> | null;
  /**
   * Identity inputs for the PP member crosswalk — `null` when the profile has
   * nothing to resolve on, which blocks. Forwarded verbatim to
   * `usePpClaimMemberSeed`; see `resolvePpMemberRecord` for the resolution order.
   */
  member: LoadedMemberForResolve | null;
  /**
   * A PP member row the host already holds (a PP-path profile's search row).
   * Used verbatim when complete, skipping the lookup — it is the same
   * `vRmiMemberSearch` row, already row-atomic, and it is the coverage span the
   * user picked in the search results themselves.
   */
  ppRecord?: PpClaimRecordLike | null;
  /**
   * The tenant the claim will be written into — the VIEWED MEMBER's `companyId`,
   * the same value the CreateClaim module sends as `X-Backend-Id`.
   *
   * SETTLED DECISION (product owner, 2026-07-30): the member's tenant is
   * authoritative for the health-plan block rules. See the `checkClaimMemberAccess`
   * call site below for the full record of the divergence from legacy this
   * represents. It is deliberate; do not repoint it at `useActingUserIpa()`.
   */
  companyId: string | undefined;
  /** PP API base URL INCLUDING `/api`. */
  ppApiBaseUrl: string;
  /** The host's async PP-bearer getter (`stableGetClaimAccessToken`). */
  getAccessToken: () => string | Promise<string>;
  /**
   * The acting user's PP role, forwarded to the claim-write provider fetch to
   * drive the BE's `SHOW_ALL_PROVIDERS_ROLES` expansion. DELIBERATELY
   * OPTIONAL — do not block the gate or treat a missing role as a failure;
   * an unresolved role just means no expansion, not "no access". See
   * claimProviderList.ts's header for the full contract.
   */
  userRole?: string;
  onCancel: () => void;
  /**
   * Called ONLY when every gate passed and a provider is selected.
   *
   * Hands back the resolved SEED alongside the provider so the caller seeds the
   * claim form from the very row this gate's decision was made on. Passing the
   * seed here — rather than letting the caller re-derive the three values — is
   * what keeps them row-atomic all the way to the PP-API request.
   */
  onProceed: (provider: ClaimProvider, seed: PpClaimMemberSeed) => void;
}

/** Legacy copy, verbatim (CreateClaimModal:430-434). */
const NO_WRITE_ACCESS_MESSAGE =
  "None of the providers associated with this user has write access to the claim module. No claim can be created.";

/** Above this many providers the list gets a filter box. */
const FILTER_THRESHOLD = 5;

/**
 * Minimum tap target. The gate is explicitly designed to run in the Chrome
 * extension side panel, i.e. on a touch-capable narrow surface, so WCAG 2.5.8's
 * 24px floor isn't enough — 44px (Apple HIG / WCAG 2.5.5 AAA) is the target.
 */
const TAP_TARGET = "min-h-[44px]";

/** Everything inside the panel that can hold focus, for the Tab trap. */
const FOCUSABLE_SELECTOR =
  'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])';

/** Reason vocabulary for the denial telemetry event. PHI-free by construction. */
type GateBlockReason =
  | "member-unresolved"
  | "member-resolve-error"
  | "health-plan-blocked"
  | "no-identity"
  | "no-tenant"
  | "forbidden"
  | "unavailable"
  | "empty";

export default function CreateClaimGate({
  memberName,
  client,
  member,
  ppRecord,
  companyId,
  ppApiBaseUrl,
  getAccessToken,
  userRole,
  onCancel,
  onProceed,
}: CreateClaimGateProps) {
  // THE MEMBER'S OWN PP ROW — the single source of `membKeyId`, `hpCode` and
  // `startDate`. All three come from one row or none of them do; there is no
  // path here that assembles them from separate places, because the claim shell
  // proc matches them as a composite key and silently returns a blank claim on a
  // miss (see usePpClaimMemberSeed's header). Values arrive already trimmed —
  // PP/EZCap serve CHAR columns, and a padded `"   "` hpCode would otherwise
  // satisfy "is the plan known?" while matching none of the payer-routing rules
  // below, silently disabling them for that member.
  const {
    status: seedStatus,
    seed,
    reload: reloadSeed,
  } = usePpClaimMemberSeed({ client, member, preset: ppRecord });
  const hpCodeTrimmed = seed?.hpCode ?? "";

  const blockMessages = useMemo(
    () =>
      // IPA SCOPE — SETTLED DECISION (product owner, 2026-07-30). The rules are
      // evaluated against the VIEWED MEMBER's tenant (`companyId`), deliberately,
      // because that is the tenant the claim is written into.
      //
      // This is a known, intentional divergence from legacy Provider Portal,
      // which read the ACTING user's `sessionStorage.ipaGroup`. For the
      // NCPN/Caremore rule the two semantics can give different answers: an
      // NCPN-acting user viewing a NON-NCPN member on a Caremore plan is BLOCKED
      // by legacy and ALLOWED here. (The plan-wide HNCC and BSPP rules ignore the
      // IPA entirely, so they are unaffected either way.)
      //
      // Both candidate semantics were reviewed and the member's tenant was chosen
      // as authoritative. Do not "fix" this back to the acting IPA
      // (`useActingUserIpa()`) without a new decision from the product owner.
      checkClaimMemberAccess(hpCodeTrimmed, companyId),
    [hpCodeTrimmed, companyId],
  );
  // Don't spend a PP-API round trip (or surface a picker) while the member is
  // unresolved, or once they are blocked on their health plan — legacy likewise
  // never reaches the hand-off in the health-plan case, and a claim form that can
  // never open has no use for a resolved provider either. This also orders the
  // two lookups: the provider list only runs once the member is known to be
  // claimable. `companyId: undefined` is what suppresses the fetch.
  const memberBlocked = seedStatus !== "resolved" || blockMessages.length > 0;

  const { status, providers, httpStatus, reload } = useClaimProviderList({
    ppApiBaseUrl,
    getAccessToken,
    companyId: memberBlocked ? undefined : companyId,
    userRole,
  });

  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [filter, setFilter] = useState("");

  // Legacy auto-selects a default the moment the list loads (CreateClaimModal:
  // 131-148) — only when the acting IPA yields exactly one provider. DERIVED,
  // not synced in an effect: an effect would leave a one-render window in which
  // a single-provider list renders with nothing selected and Continue disabled,
  // and this repo's convention is to avoid effects for values that are pure
  // functions of existing state. `selectedKey` (the user's own pick) always wins.
  const autoSelectedKey = useMemo(() => {
    if (status !== "ready") return null;
    const dflt = selectDefaultClaimProvider(providers);
    return dflt ? claimProviderKey(dflt) : null;
  }, [status, providers]);
  const effectiveKey = selectedKey ?? autoSelectedKey;

  // The filter input only EXISTS above the threshold. Below it a previously
  // typed query must be inert rather than merely invisible — otherwise a retry
  // that returns fewer providers unmounts the input while its query still
  // filters, stranding the user on "No providers match that filter." with no
  // input to clear. Derived rather than reset in an effect, so the stale state is
  // never observable at all.
  const filterVisible = providers.length > FILTER_THRESHOLD;
  const activeFilter = filterVisible ? filter.trim().toLowerCase() : "";

  const visibleProviders = useMemo(() => {
    if (!activeFilter) return providers;
    return providers.filter(
      (p) =>
        claimProviderLabel(p).toLowerCase().includes(activeFilter) ||
        claimProviderAddress(p).toLowerCase().includes(activeFilter),
    );
  }, [providers, activeFilter]);

  // SELECTION ⊆ RENDERED LIST, structurally. `selected` is resolved against
  // `visibleProviders` — the exact array the radiogroup renders — not against the
  // full `providers`. Resolving it against `providers` made it possible to
  // select "Alpha Clinic", type a filter that hides it, and still submit Alpha:
  // its GUID becomes the claim's billing provider / facility / provider NPI, so
  // the user would file the claim under a provider that was not on screen.
  // Deriving from the rendered array makes that unrepresentable rather than
  // merely guarded — there is no state to keep in sync and no effect to race.
  const selected = visibleProviders.find((p) => claimProviderKey(p) === effectiveKey) ?? null;
  // A real (still-in-the-list) pick that the current filter is hiding. Worth
  // saying out loud, or the disabled Continue looks like a bug.
  const selectionHiddenByFilter =
    effectiveKey !== null &&
    selected === null &&
    providers.some((p) => claimProviderKey(p) === effectiveKey);

  // Clear any prior pick AND the filter alongside a retry, so the re-fetched
  // list starts clean and can auto-select its default again.
  const retry = () => {
    setSelectedKey(null);
    setFilter("");
    reload();
  };

  // ── Denial telemetry ──────────────────────────────────────────────────────
  // This gate is the only enforcement point for OPENING a seeded claim form (and
  // the only one at all for the payer-routing health-plan rules), so every denial
  // is a security-relevant event — and until now nothing about it was observable,
  // including the operationally important question of whether the "endpoint not
  // deployed" (404) state has cleared after a BE deploy.
  //
  // PHI RULE (ACT-1037, see telemetry/appInsights.ts): the reason is drawn from a
  // fixed vocabulary and the only other dimension is a numeric HTTP status. NO
  // hpCode (it is a member plan attribute), no member id, no member name.
  // `"resolving"` reports NOTHING — it is a transient loading state, not a
  // denial. It must also short-circuit before `status` is consulted: while the
  // member is unresolved the provider fetch is suppressed with
  // `companyId: undefined`, so `status` is the `"no-tenant"` sentinel, and
  // reporting THAT would be a wrong diagnosis on every single member.
  const blockReason: GateBlockReason | null =
    seedStatus === "resolving"
      ? null
      : seedStatus === "unresolved"
        ? "member-unresolved"
        : seedStatus === "error"
          ? "member-resolve-error"
          : blockMessages.length > 0
            ? "health-plan-blocked"
            : status === "no-identity" ||
                status === "no-tenant" ||
                status === "forbidden" ||
                status === "unavailable" ||
                status === "empty"
              ? status
              : null;

  useEffect(() => {
    if (!blockReason) return;
    trackEvent("create_claim_gate_blocked", {
      reason: blockReason,
      // Falsy (0 / undefined) is dropped: 0 is the "no usable HTTP status"
      // sentinel and undefined means the failure never reached HTTP at all.
      httpStatus: httpStatus || undefined,
    });
  }, [blockReason, httpStatus]);

  // ── Modal a11y: initial focus, Escape, Tab trap ───────────────────────────
  const panelRef = useRef<HTMLDivElement>(null);

  // Move focus INTO the dialog on mount. The panel itself (tabIndex={-1}) rather
  // than its first control: the content is still "loading" at this point, and
  // landing on the panel lets a screen reader announce the title before the user
  // Tabs to anything actionable.
  useEffect(() => {
    panelRef.current?.focus();
  }, []);

  // Escape closes. Bound at the document (not the overlay's onKeyDown) so it
  // works even if focus has somehow escaped the panel. Unlike PpLoginModal /
  // SessionExpiryModal — which are deliberately non-dismissable because
  // abandoning them abandons a session — cancelling this gate is free and
  // reversible, so it gets the standard dialog dismissal.
  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onCancel();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onCancel]);

  /**
   * Tab trap. `aria-modal` tells assistive tech the rest of the page is inert
   * but does nothing for keyboard focus order, and the background here is a full
   * member profile full of tabbable controls — so Tab must be cycled by hand.
   */
  const handlePanelKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "Tab") return;
    const panel = panelRef.current;
    if (!panel) return;
    const focusables = Array.from(
      panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
    ).filter((el) => !el.hasAttribute("disabled") && el.tabIndex >= 0);
    if (focusables.length === 0) {
      e.preventDefault();
      panel.focus();
      return;
    }
    const first = focusables[0]!;
    const last = focusables[focusables.length - 1]!;
    const active = document.activeElement as HTMLElement | null;
    if (e.shiftKey) {
      if (active === first || active === panel) {
        e.preventDefault();
        last.focus();
      }
    } else if (active === last) {
      e.preventDefault();
      first.focus();
    }
  };

  /**
   * ARIA APG radio-group keyboard contract: arrow keys (both axes), Home and End
   * move focus AND check the newly focused radio — for a radio group, moving is
   * selecting. Combined with the roving tabindex below (only the checked radio is
   * tabbable), this makes the picker operable without a pointer, which the
   * side-panel layout otherwise quietly assumed.
   */
  const handleRadioGroupKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (
      e.key !== "ArrowDown" &&
      e.key !== "ArrowUp" &&
      e.key !== "ArrowRight" &&
      e.key !== "ArrowLeft" &&
      e.key !== "Home" &&
      e.key !== "End"
    ) {
      return;
    }
    const radios = Array.from(
      e.currentTarget.querySelectorAll<HTMLElement>('[role="radio"]'),
    );
    if (radios.length === 0) return;
    const currentIndex = radios.indexOf(document.activeElement as HTMLElement);
    e.preventDefault();
    let nextIndex: number;
    if (currentIndex === -1) {
      // Focus isn't on a radio (the group itself, or the "no match" placeholder).
      // Enter at the top rather than applying the delta from a fictitious
      // position — an ArrowUp here should not jump to the LAST row.
      nextIndex = e.key === "End" ? radios.length - 1 : 0;
    } else if (e.key === "ArrowDown" || e.key === "ArrowRight") {
      nextIndex = (currentIndex + 1) % radios.length;
    } else if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
      nextIndex = (currentIndex - 1 + radios.length) % radios.length;
    } else if (e.key === "Home") {
      nextIndex = 0;
    } else {
      nextIndex = radios.length - 1;
    }
    const next = radios[nextIndex];
    next?.focus();
    const key = next?.dataset.providerKey;
    if (key) setSelectedKey(key);
  };

  const body = (() => {
    // The member lookup runs FIRST and its states are reported ahead of the
    // provider list's, because the provider fetch is deliberately suppressed
    // until the member resolves (see `memberBlocked`) — so `status` carries the
    // `"no-tenant"` sentinel throughout and must not be rendered here.
    if (seedStatus === "resolving") {
      return (
        <p className="m-0 text-sm text-muted-foreground" role="status">
          Looking up this member's Provider Portal record…
        </p>
      );
    }
    if (seedStatus === "error") {
      return (
        <Blocked
          title="Couldn't look up this member's Provider Portal record"
          lines={[
            "Creating a claim needs this member's Provider Portal coverage record, and that lookup didn't complete.",
            "No claim can be created until it succeeds.",
          ]}
          onRetry={reloadSeed}
        />
      );
    }
    if (seedStatus === "unresolved") {
      return (
        <Blocked
          title="Can't create a claim for this member yet"
          lines={[
            // The honest diagnosis, replacing an earlier "their coverage start
            // date isn't available in the current view" — which pointed at a
            // symptom (a field this view didn't carry) instead of the cause, and
            // implied a remedy (reopen from Member Search) that fixed nothing.
            "This member couldn't be matched to a Provider Portal coverage record, so there's nothing to file a claim against.",
            "A claim needs the member's Provider Portal record to identify them to the claims system. If you expect this member to have one, contact support so the member record can be checked.",
          ]}
          onRetry={reloadSeed}
        />
      );
    }
    if (blockMessages.length > 0) {
      return <Blocked title="This claim must be submitted to the health plan" lines={blockMessages} />;
    }
    switch (status) {
      case "loading":
        return (
          <p className="m-0 text-sm text-muted-foreground" role="status">
            Checking your claim access…
          </p>
        );
      case "no-identity":
        return (
          <Blocked
            title="Provider Portal sign-in required"
            lines={[
              "Creating a claim uses your Provider Portal account, and no active Provider Portal session was found.",
              "Sign in to Provider Portal and try again.",
            ]}
          />
        );
      case "no-tenant":
        // DISTINCT from no-identity on purpose. This member simply has no
        // Astrana IPA/tenant to file against (a Pathways record whose ipaCode is
        // "NA", or a connect-only member). Reporting it as "sign in to Provider
        // Portal" is a wrong diagnosis that sends an already-signed-in user to
        // re-authenticate and then raise a ticket when it doesn't help.
        return (
          <Blocked
            title="No Astrana IPA on record for this member"
            lines={[
              "Claims are filed against an Astrana IPA, and this member isn't associated with one in the data available here.",
              "This isn't a sign-in problem — your account is fine. If you expect this member to be covered by an Astrana IPA, contact support so the member record can be checked.",
            ]}
          />
        );
      case "forbidden":
        return (
          <Blocked
            title="Not authorized to create claims"
            lines={[
              "Provider Portal declined this request for your account (HTTP 403).",
              "If you believe you should have claim access, contact your Provider Portal administrator.",
            ]}
          />
        );
      case "unavailable":
        return (
          <Blocked
            title="Create Claim is unavailable"
            lines={[
              httpStatus === 404
                ? "The Provider Portal claim-access service isn't available in this environment yet, so claim access can't be verified."
                : // `httpStatus` is falsy both when the failure never reached
                  // HTTP (network/abort) and for the no-usable-status sentinel
                  // (a 200 with an unusable body) — neither has an HTTP code
                  // worth quoting, and quoting the sentinel's source status
                  // would print the nonsense "(HTTP 200)".
                  `Couldn't verify your claim access with Provider Portal${httpStatus ? ` (HTTP ${httpStatus})` : ""}.`,
              "No claim can be created until this check succeeds.",
            ]}
            onRetry={retry}
          />
        );
      case "empty":
        return <Blocked title="No claim access" lines={[NO_WRITE_ACCESS_MESSAGE]} />;
      case "ready":
        return (
          <div className="flex flex-col gap-2">
            <span
              id="create-claim-gate-provider-label"
              className="text-xs font-semibold uppercase tracking-wide text-foreground/80"
            >
              Select a provider
            </span>
            {filterVisible && (
              <input
                type="search"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Filter providers…"
                aria-label="Filter providers"
                className={`${TAP_TARGET} rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-ring`}
              />
            )}
            {selectionHiddenByFilter && (
              <p role="status" className="m-0 text-xs text-foreground/80">
                The provider you selected is hidden by the current filter. Clear the
                filter to continue with it, or pick one from the list below.
              </p>
            )}
            <div
              role="radiogroup"
              aria-labelledby="create-claim-gate-provider-label"
              onKeyDown={handleRadioGroupKeyDown}
              className="flex max-h-64 flex-col gap-1 overflow-y-auto rounded-lg border border-border p-1"
            >
              {visibleProviders.length === 0 ? (
                <p className="m-0 px-2 py-3 text-sm text-muted-foreground">
                  No providers match that filter.
                </p>
              ) : (
                visibleProviders.map((p, i) => {
                  const key = claimProviderKey(p);
                  const isSelected = key === effectiveKey;
                  const address = claimProviderAddress(p);
                  return (
                    <button
                      key={key}
                      type="button"
                      role="radio"
                      aria-checked={isSelected}
                      // Roving tabindex (ARIA APG): the group is ONE tab stop.
                      // The checked radio is the tabbable one; with nothing
                      // checked the first row is, so the group is always
                      // reachable by Tab.
                      tabIndex={
                        isSelected || (selected === null && i === 0) ? 0 : -1
                      }
                      data-provider-key={key}
                      onClick={() => setSelectedKey(key)}
                      // `shrink-0` is load-bearing, not cosmetic. The list is a
                      // flex COLUMN with a capped height (`max-h-64`), so once
                      // the rows total more than 256px the free space goes
                      // negative and every row — `flex-shrink: 1` by default —
                      // is squashed toward its `min-h-[44px]` floor BEFORE the
                      // container's `overflow-y-auto` ever scrolls. A row whose
                      // provider name wraps to two lines needs ~60px, so the
                      // address sub-line spills out of the squashed 44px box
                      // (buttons don't clip) and the next row's name paints on
                      // top of it — AM-5824's overlapping-text list. It only
                      // reproduces where the name actually wraps, i.e. in the
                      // narrow Chrome extension side panel, which is why a
                      // wide-window check looks clean.
                      className={`${TAP_TARGET} flex shrink-0 flex-col items-start justify-center gap-0.5 rounded-md px-2.5 py-2 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                        isSelected
                          ? "bg-primary/10 text-foreground ring-1 ring-primary"
                          : "text-foreground hover:bg-accent"
                      }`}
                    >
                      <span className="text-sm font-medium leading-snug">
                        {claimProviderLabel(p)}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {p.RmiCompanyId.trim()}
                        {p.RmiClass ? ` · ${p.RmiClass.trim()}` : ""}
                        {address ? ` · ${address}` : ""}
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        );
    }
  })();

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/70 p-4 backdrop-blur-sm"
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-claim-gate-title"
        aria-describedby="create-claim-gate-desc"
        // This is host-authored, not an @astrana/ui component — it renders
        // entirely with the host's own `:root`/`.dark` semantic tokens (see
        // this file's className usage throughout) and imports nothing from
        // @astrana/ui. Without this, `useAstranaPortalScope`'s dialog scanner
        // matches this element on `role="dialog"` alone (Radix's own portal
        // wrapper carries no distinguishing marker, so that's the best signal
        // it has) and wrongly tags this panel's `<body>`-level wrapper
        // `.astrana-ui`, which in dark mode repaints the whole popup with the
        // LIBRARY's pure-gray palette/font instead of the host's — AM-5824's
        // "completely distorted" Select Provider popup.
        data-astrana-ui-exempt
        tabIndex={-1}
        onKeyDown={handlePanelKeyDown}
        className="flex w-full max-w-md flex-col gap-4 rounded-2xl border border-border bg-card p-5 text-card-foreground shadow-2xl focus:outline-none sm:p-6"
      >
        <div className="flex flex-col gap-1">
          <h2
            id="create-claim-gate-title"
            className="m-0 text-lg font-bold text-foreground"
          >
            Create Claim
          </h2>
          <p id="create-claim-gate-desc" className="m-0 text-sm text-muted-foreground">
            {memberName ? `For ${memberName}. ` : ""}
            Claims are filed against one of your Provider Portal providers.
          </p>
        </div>

        {body}

        <div className="flex items-center justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onCancel}
            // Full-strength `text-foreground` on a bordered surface, not
            // `text-muted-foreground`: in dark mode --muted-foreground
            // (215 20% 55%) on --card (220 8% 15%) computes to ≈4.3:1, just
            // under WCAG AA's 4.5:1 for body text.
            className={`${TAP_TARGET} rounded-lg border border-border bg-card px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-ring`}
          >
            Cancel
          </button>
          <button
            type="button"
            // The ONLY path to the form. Disabled unless a provider from the
            // acting IPA's claim-write list is selected AND currently VISIBLE in
            // the list — mirrors legacy's `disabled={!selectedProvider}` on every
            // control, and `selected` can only ever be a rendered row (see its
            // derivation above). Every non-"ready" status renders a body with no
            // picker at all, so `selected` is null and this stays disabled.
            //
            // `seed` is re-checked (not merely implied by `selected`): it is the
            // member half of the decision, and it is what the caller seeds the
            // form with. A provider without a seed must never reach onProceed.
            disabled={!selected || !seed}
            onClick={() => {
              if (selected && seed) onProceed(selected, seed);
            }}
            className={`${TAP_TARGET} inline-flex items-center justify-center rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-60`}
          >
            Continue
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function Blocked({
  title,
  lines,
  onRetry,
}: {
  title: string;
  lines: string[];
  onRetry?: () => void;
}) {
  return (
    <div
      role="alert"
      className="flex flex-col gap-2 rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-3"
    >
      <p className="m-0 text-sm font-semibold text-foreground">{title}</p>
      {lines.map((line, i) => (
        <p key={i} className="m-0 text-sm text-foreground/80">
          {line}
        </p>
      ))}
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className={`${TAP_TARGET} self-start rounded-md border border-border bg-card px-3 py-2 text-xs font-medium text-foreground transition-colors hover:bg-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-ring`}
        >
          Try again
        </button>
      )}
    </div>
  );
}
